import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { HelpCircle, X } from 'lucide-react';
import { useTranslation } from '../i18n';
import './PageGuide.css';

const STORAGE_PREFIX = 'ignite-guide-';

/**
 * PageGuide — contextual help button + bottom-sheet modal.
 *
 * On first visit: renders a glowing HelpCircle button.
 * After user reads the guide ("Got it"): button disappears entirely.
 * Reset via Settings → "Reset Page Guides".
 *
 * Feature descriptions support HTML for rich formatting (<b>, <em>, <span>, etc.).
 *
 * Usage:
 *   <PageGuide screenKey="prayer.calendar" />
 */
export default function PageGuide({ screenKey }) {
  const { t } = useTranslation('guide');
  const storageKey = STORAGE_PREFIX + screenKey;

  // Read dismissed state fresh each render so it reacts to screenKey changes
  const isDismissed = localStorage.getItem(storageKey) === '1';
  const [open, setOpen] = useState(false);
  const [justDismissed, setJustDismissed] = useState(null);

  if (isDismissed || justDismissed === screenKey) return null;

  function handleGotIt() {
    localStorage.setItem(storageKey, '1');
    setOpen(false);
    setJustDismissed(screenKey);
  }

  const title = t(`${screenKey}.title`) || t('aboutPage');
  const overview = t(`${screenKey}.overview`);
  const features = t(`${screenKey}.features`) || [];

  const modal = open ? ReactDOM.createPortal(
    <div className="pg-overlay" onClick={() => setOpen(false)}>
      <div className="pg-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pg-header">
          <h2 className="pg-title">{title}</h2>
          <button className="pg-close" onClick={() => setOpen(false)} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="pg-body">
          <p className="pg-overview" dangerouslySetInnerHTML={{ __html: overview }} />

          <div className="pg-features">
            {Array.isArray(features) && features.map((f, i) => (
              <div className={`pg-feature${f.accent ? ` pg-accent-${f.accent}` : ''}`} key={i}>
                <div className="pg-feature-title">
                  {f.icon && <span className="pg-feature-icon">{f.icon}</span>}
                  {f.title}
                </div>
                <p className="pg-feature-desc" dangerouslySetInnerHTML={{ __html: f.desc }} />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pg-footer">
          <button className="btn btn-primary pg-got-it" onClick={handleGotIt}>
            {t('gotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        className="pg-btn pg-btn-glow"
        onClick={() => setOpen(true)}
        aria-label={t('aboutPage')}
        title={t('aboutPage')}
      >
        <HelpCircle size={20} strokeWidth={1.75} />
      </button>
      {modal}
    </>
  );
}

/**
 * Utility: clear all page guide dismissals (for Settings reset).
 * Returns the number of keys cleared.
 */
export function resetAllPageGuides() {
  let count = 0;
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  keys.forEach(k => { localStorage.removeItem(k); count++; });
  return count;
}
